import React, { useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import FilterChild from "./filterChild";
import "./index.css";
import Debounce from "../../utils/Debounce";

const debounce = new Debounce(200);

const FilterBar = ({
  children,
  disabled,
  currentField,
  setCurrentField,
  isLoading,
  clearWho,
  clearWhere,
}: {
  children: React.ReactElement | (React.ReactElement | false)[];
  disabled: boolean;
  currentField: string | null;
  setCurrentField: (value: string | null) => void;
  isLoading: boolean;
  clearWho: () => void;
  clearWhere: () => void;
}) => {
  const [filteredItems, setFilteredItems] = useState<React.ReactElement[]>([]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  useEffect(() => {
    if (!(children as any)?.length) {
      return setFilteredItems([children as any]);
    }

    const newFilteredItems = (children as any).filter(
      (child: any) => child !== false,
    ) as React.ReactElement[];

    return setFilteredItems(newFilteredItems);
  }, [children]);

  useEffect(() => {
    if (currentField === null) {
      setSelectedIndex(null);
    } else {
      filteredItems.forEach((child, index) => {
        if (child.props.property === currentField) {
          setSelectedIndex(index);
        }
      });
    }
  }, [currentField, filteredItems]);

  useEffect(() => {
    if (!currentField) return;

    if (selectedIndex === filteredItems.length - 1) {
      // Add listener for enter key down event
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [filteredItems, selectedIndex, currentField]);

  useEffect(() => {
    if (!currentField) return;

    const onTabHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setCurrentField(null);
        return;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        if (selectedIndex === null) {
          return;
        }

        if (e.shiftKey) {
          const previousIndex = selectedIndex - 1;
          if (previousIndex < 0) {
            setSelectedIndex(null);
            setCurrentField(null);
          } else {
            e.preventDefault();
            debounce.call(() => {
              setSelectedIndex(previousIndex);
              setCurrentField(filteredItems[previousIndex].props.property);
            });
          }
        } else {
          const nextIndex = selectedIndex + 1;
          if (nextIndex < filteredItems.length) {
            e.preventDefault();
            debounce.call(() => {
              setSelectedIndex(nextIndex);
              setCurrentField(filteredItems[nextIndex].props.property);
            });
          } else {
            setSelectedIndex(null);
            setCurrentField(null);
          }
        }
      }
    };

    document.addEventListener("keydown", onTabHandler);

    return () => {
      document.removeEventListener("keydown", onTabHandler);
    };
  }, [currentField, filteredItems, selectedIndex, setCurrentField]);
  return (
    <div
      id="filterBar"
      className="searchBar"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={disabled ? -1 : 0}
      style={{
        zIndex: 20,
        display: "flex",
        borderRadius: 32.5,
        backgroundColor: disabled ? "#F3F4F6" : "#FBFCFC",
        pointerEvents: disabled ? "none" : "auto",
        border: !currentField || disabled ? "1px solid #E2E8F0" : undefined,
        boxShadow: "0px 10px 15px -3px rgba(226, 232, 240, 0.4)",
      }}
      onFocus={(e) => {
        // Only auto-focus first item when tabbing in from outside (keyboard navigation)
        // Don't auto-focus on mouse clicks within the filter bar
        if (
          !currentField &&
          filteredItems.length > 0 &&
          e.relatedTarget &&
          !e.currentTarget.contains(e.relatedTarget as Node)
        ) {
          setSelectedIndex(0);
          setCurrentField(filteredItems[0].props.property);
        }
      }}
    >
      {isLoading && (
        <div
          style={{
            backgroundColor: "rgba(15, 44, 82, 0.5)",
            zIndex: 1000,
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ThreeDots width={120} color="rgb(255, 255, 255)" />
        </div>
      )}

      {filteredItems.map((child, index) => {
        const selected = !currentField
          ? undefined
          : currentField === child.props.property;

        let nextIsSelected = false;
        if (selectedIndex !== null) {
          nextIsSelected = index + 1 === selectedIndex;
        }
        const isLastItem = index === filteredItems.length - 1;

        let nextIsFirst = false;
        const nextItem = filteredItems[index + 1];
        if (nextItem && nextItem.props?.first === true) {
          nextIsFirst = true;
        }
        let showDivider = true;
        if (selected) showDivider = false;
        if (nextIsSelected) showDivider = false;
        if (isLastItem) showDivider = false;
        if (nextIsFirst) showDivider = false;

        let backgroundColor = "#fff";
        if (child.props.disabled) {
          backgroundColor = "#D9D9D9";
        } else if (currentField && currentField !== child.props.property) {
          backgroundColor = "#EDEDED";
        }

        return (
          <div
            key={`FilterChild-${child.props.property}`}
            style={{
              order: index,
              display: "flex",
              alignItems: "center",
            }}
            className="filterChildContainer"
          >
            <FilterChild
              property={child.props.property}
              selected={selected}
              select={(value: boolean) => {
                if (value) {
                  setCurrentField(child.props.property);
                } else {
                  setCurrentField(null);
                }
              }}
              label={child.props.label}
              placeholder={child.props.placeholder}
              value={child.props.value}
              type={child.props.type}
              first={child.props.first}
              last={child.props.last}
              disabled={disabled}
              zIndex={child.props.zIndex}
              inFocus={child.props.inFocus}
              className={child.props.className}
              clear={child.props.label === "WHO" ? clearWho : clearWhere}
            >
              {child}
            </FilterChild>

            {showDivider && (
              <div
                style={{
                  width: 1,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor,
                }}
              >
                <div
                  style={{
                    height: "72%",
                    width: 1,
                    backgroundColor: "#E2E8F0",
                    opacity: disabled ? 0.2 : 1,
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FilterBar;
